import { Injectable, Renderer2, RendererFactory2 } from "@angular/core";

export interface ITooltipPosition {
    left: number;
    top: number;
    direction?: string
}

@Injectable({
    providedIn: 'root',
})
export class TooltipService {

    private renderer: Renderer2;

    private tooltip: HTMLElement;
    private textElement: Text;

    constructor(rendererFactory: RendererFactory2) {
        this.renderer = rendererFactory.createRenderer(null, null);
    }

    private create(): void {
        this.tooltip = this.renderer.createElement('div');
        this.textElement = this.renderer.createText("");

        this.renderer.appendChild(this.tooltip, this.textElement);

        this.renderer.appendChild(document.body, this.tooltip);
        this.renderer.addClass(this.tooltip, 'angular-tooltip');
    }

    show(title: string, position?: ITooltipPosition): void {
        if (!this.tooltip) {
            this.create();
        }

        this.renderer.setProperty(this.textElement, 'textContent', title);

        this.renderer.addClass(this.tooltip, 'angular-tooltip-fade-in');

        if (position) {
            this.setPosition(this.tooltip, position);
        }
    }

    hide(): void {
        if (!this.tooltip) {
            return;
        }

        this.renderer.removeClass(this.tooltip, 'angular-tooltip-fade-in');
    }

    private remove(tooltip: HTMLElement): void {
        if (!tooltip) {
            return;
        }

        tooltip.remove();
    }

    setPosition(tooltip: HTMLElement, position: ITooltipPosition, boundingElement?: HTMLElement): void {
        this.setDirectionAdjustedPosition(tooltip, position);

        tooltip.classList.add('angular-tooltip-' + position.direction);
        tooltip.style.transform = `translate(${Math.round(position.left)}px,${Math.round(position.top)}px)` ;
    }

    // ---

    private setDirectionAdjustedPosition(tooltip: HTMLElement, pos: ITooltipPosition): void {
        const arrow_padding = 12;
        var tooltipBounding = tooltip.getBoundingClientRect();
        pos.direction = pos.direction || "top";

        var scrollLeft = 0;
        var scrollTop = 0;

        if (this.stringStartsWith(pos.direction, 'left')) {
            pos.left = pos.left - tooltipBounding.width - (arrow_padding / 2) + scrollLeft;
        }
        else if (this.stringStartsWith(pos.direction, 'right')) {
            pos.left = pos.left + (arrow_padding / 2) + scrollLeft;
        }
        else if (this.stringContains(pos.direction, 'left')) {
            pos.left = pos.left - tooltipBounding.width + arrow_padding + scrollLeft;
        }
        else if (this.stringContains(pos.direction, 'right')) {
            pos.left = pos.left - arrow_padding + scrollLeft;
        }
        else {
            pos.left = pos.left - (tooltipBounding.width / 2) + scrollLeft;
        }

        // calculate the top position
        if (this.stringStartsWith(pos.direction, 'top')) {
            pos.top = pos.top - tooltipBounding.height - (arrow_padding / 2) + scrollTop;
        }
        else if (this.stringStartsWith(pos.direction, 'bottom')) {
            pos.top = pos.top + (arrow_padding / 2) + scrollTop;
        }
        else if (this.stringContains(pos.direction, 'top')) {
            pos.top = pos.top - tooltipBounding.height + arrow_padding + scrollTop;
        }
        else if (this.stringContains(pos.direction, 'bottom')) {
            pos.top = pos.top - arrow_padding + scrollTop;
        }
        else {
            pos.top = pos.top + (tooltipBounding.height / 2) - (tooltipBounding.height / 2) + scrollTop;
        }
    }

    private getToolipPositionFromBoundingElement(tooltip: HTMLElement, position: ITooltipPosition, boundingElement?: HTMLElement): ITooltipPosition {
        //this.setDirection();

        position.direction = position.direction || "top";

        var tooltipBounding = tooltip.getBoundingClientRect();
        var elBounding = boundingElement ? boundingElement.getBoundingClientRect() : { x: 0, y: 0, width: 0, height: 0, left: 0, top: 0 };
        var scrollLeft = window.scrollX || document.documentElement.scrollLeft;
        var scrollTop = window.scrollY || document.documentElement.scrollTop;
        var arrow_padding = 12;
        var pos: ITooltipPosition = { left: 0, top: 0 };
        var newDirection = null;

        // calculate the left position
        if (this.stringStartsWith(position.direction, 'left')) {
            pos.left = elBounding.left - tooltipBounding.width - (arrow_padding / 2) + scrollLeft;
        }
        else if (this.stringStartsWith(position.direction, 'right')) {
            pos.left = elBounding.left + elBounding.width + (arrow_padding / 2) + scrollLeft;
        }
        else if (this.stringContains(position.direction, 'left')) {
            pos.left = elBounding.left - tooltipBounding.width + arrow_padding + scrollLeft;
        }
        else if (this.stringContains(position.direction, 'right')) {
            pos.left = elBounding.left + elBounding.width - arrow_padding + scrollLeft;
        }
        else {
            pos.left = elBounding.left + (elBounding.width / 2) - (tooltipBounding.width / 2) + scrollLeft;
        }

        // calculate the top position
        if (this.stringStartsWith(position.direction, 'top')) {
            pos.top = elBounding.top - tooltipBounding.height - (arrow_padding / 2) + scrollTop;
        }
        else if (this.stringStartsWith(position.direction, 'bottom')) {
            pos.top = elBounding.top + elBounding.height + (arrow_padding / 2) + scrollTop;
        }
        else if (this.stringContains(position.direction, 'top')) {
            pos.top = elBounding.top - tooltipBounding.height + arrow_padding + scrollTop;
        }
        else if (this.stringContains(position.direction, 'bottom')) {
            pos.top = elBounding.top + elBounding.height - arrow_padding + scrollTop;
        }
        else {
            pos.top = elBounding.top + (elBounding.height / 2) - (tooltipBounding.height / 2) + scrollTop;
        }

        // Todo?
        // check if the tooltip is outside the bounds of the window
        //if (this.fixedPosition) {
        //    if (pos.left < scrollLeft) {
        //        newDirection = this.direction.replace('left', 'right');
        //    }
        //    else if ((pos.left + tooltipBounding.width) > (window.innerWidth + scrollLeft)) {
        //        newDirection = this.direction.replace('right', 'left');
        //    }

        //    if (pos.top < scrollTop) {
        //        newDirection = this.direction.replace('top', 'bottom');
        //    }
        //    else if ((pos.top + tooltipBounding.height) > (window.innerHeight + scrollTop)) {
        //        newDirection = this.direction.replace('bottom', 'top');
        //    }

        //    if (newDirection) {
        //        //return this.calculatePosition(this.tooltip, newDirection);
        //    }
        //}

        //pos.left += 'px';
        //pos.top += 'px';
        //pos.direction = this.direction;

        return pos;
    }

    private stringStartsWith(searchString, findString): boolean {
        if (!searchString) {
            console.warn("stringStartsWith searchString null", searchString, findString);
            return false;
        }

        return searchString.substr(0, findString.length) === findString;
    }

    private stringContains(searchString, findString): boolean {
        return searchString.indexOf(findString) !== -1;
    }
}
